import React from "react";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from "currency-symbol-map";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/segments/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  currency: string;
  setCurrency: (e: string) => void;
}

const SegmentRulesBetTab: React.FC<Props> = ({ currency, setCurrency, fieldErrors, clearFieldError }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currenciesList = useAppSelector((store) => store.settings.currencies);

  const itemPayload = useAppSelector(
    (store) =>
      store.segments.payload?.betRules?.filter(
        (el) => el.currency === currency
      )[0]
  );
  const payloadFull = useAppSelector((store) => store.segments.payload);

  const handleData = (
    key: string,
    value: string | boolean | number | number[] | string[]
  ) => {
    clearFieldError(key);
    const existingList = payloadFull?.betRules || [];

    const existingRule = existingList.find(
      (el) => el.currency === currency
    ) || { currency };

    const updatedRule = {
      ...existingRule,
      [key]: parseFloat(value as string),
    };

    const updatedList = [
      ...existingList.filter((el) => el.currency !== currency),
      updatedRule,
    ];
    //   @ts-ignore
    dispatch(handleUpdateFields({ betRules: updatedList }));
  };

  return (
    <div className="rules-payments-wrapper">
      <div className="game-modal-tabs small">
        {currenciesList.map((el) => (
          <div
            className={`tab-item ${currency === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setCurrency(el);
            }}
          >
            <div className="currency-tab-item">
              <div className="currency">{getSymbolFromCurrency(el)}</div> {el}
            </div>
          </div>
        ))}
      </div>
      <div className="segment-rules-form-wrapper">
        <div className="segments-rules-form-box">
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.betAmountMin || !!itemPayload?.betAmountMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.betAmountMin === 0 ||
                    itemPayload?.betAmountMin === undefined) &&
                  (itemPayload?.betAmountMax === 0 ||
                    itemPayload?.betAmountMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            betAmountMin: isDisabled ? 1 : 0,
                            betAmountMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            betAmountMin: isDisabled ? 1 : 0,
                            betAmountMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["betAmountMin"])}
              value={itemPayload?.betAmountMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-betAmountMin"
              onChange={(e) => handleData("betAmountMin", e)}
              disabled={itemPayload?.betAmountMin === 0}
            />
            <span className="txt">{t("betAmount")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["betAmountMax"])}
              value={itemPayload?.betAmountMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-betAmountMax"
              onChange={(e) => handleData("betAmountMax", e)}
              disabled={itemPayload?.betAmountMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.betCountMin || !!itemPayload?.betCountMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.betCountMin === 0 ||
                    itemPayload?.betCountMin === undefined) &&
                  (itemPayload?.betCountMax === 0 ||
                    itemPayload?.betCountMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            betCountMin: isDisabled ? 1 : 0,
                            betCountMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            betCountMin: isDisabled ? 1 : 0,
                            betCountMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["betCountMin"])}
              value={itemPayload?.betCountMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-betCountMin"
              onChange={(e) => handleData("betCountMin", e)}
              disabled={itemPayload?.betCountMin === 0}
            />
            <span className="txt">{t("betCount")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["betCountMax"])}
              value={itemPayload?.betCountMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-betCountMax"
              onChange={(e) => handleData("betCountMax", e)}
              disabled={itemPayload?.betCountMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={!!itemPayload?.betSumMin || !!itemPayload?.betSumMax}
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.betSumMin === 0 ||
                    itemPayload?.betSumMin === undefined) &&
                  (itemPayload?.betSumMax === 0 ||
                    itemPayload?.betSumMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            betSumMin: isDisabled ? 1 : 0,
                            betSumMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            betSumMin: isDisabled ? 1 : 0,
                            betSumMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["betSumMin"])}
              value={itemPayload?.betSumMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-betSumMin"
              onChange={(e) => handleData("betSumMin", e)}
              disabled={itemPayload?.betSumMin === 0}
            />
            <span className="txt">{t("betSum")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["betSumMax"])}
              value={itemPayload?.betSumMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-betSumMax"
              onChange={(e) => handleData("betSumMax", e)}
              disabled={itemPayload?.betSumMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={!!itemPayload?.betAvgMin || !!itemPayload?.betAvgMax}
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.betAvgMin === 0 ||
                    itemPayload?.betAvgMin === undefined) &&
                  (itemPayload?.betAvgMax === 0 ||
                    itemPayload?.betAvgMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            betAvgMin: isDisabled ? 1 : 0,
                            betAvgMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            betAvgMin: isDisabled ? 1 : 0,
                            betAvgMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["betAvgMin"])}
              value={itemPayload?.betAvgMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-betAvgMin"
              onChange={(e) => handleData("betAvgMin", e)}
              disabled={itemPayload?.betAvgMin === 0}
            />
            <span className="txt">{t("betAvg")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["betAvgMax"])}
              value={itemPayload?.betAvgMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-betAvgMax"
              onChange={(e) => handleData("betAvgMax", e)}
              disabled={itemPayload?.betAvgMax === 0}
            />
          </div>
        </div>
        <div className="segments-rules-form-box">
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.winAmountMin || !!itemPayload?.winAmountMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.winAmountMin === 0 ||
                    itemPayload?.winAmountMin === undefined) &&
                  (itemPayload?.winAmountMax === 0 ||
                    itemPayload?.winAmountMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            winAmountMin: isDisabled ? 1 : 0,
                            winAmountMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            winAmountMin: isDisabled ? 1 : 0,
                            winAmountMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["winAmountMin"])}
              value={itemPayload?.winAmountMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-winAmountMin"
              onChange={(e) => handleData("winAmountMin", e)}
              disabled={itemPayload?.winAmountMin === 0}
            />
            <span className="txt">{t("winAmount")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["winAmountMax"])}
              value={itemPayload?.winAmountMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-winAmountMax"
              onChange={(e) => handleData("winAmountMax", e)}
              disabled={itemPayload?.winAmountMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={
                !!itemPayload?.winCountMin || !!itemPayload?.winCountMax
              }
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.winCountMin === 0 ||
                    itemPayload?.winCountMin === undefined) &&
                  (itemPayload?.winCountMax === 0 ||
                    itemPayload?.winCountMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            winCountMin: isDisabled ? 1 : 0,
                            winCountMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            winCountMin: isDisabled ? 1 : 0,
                            winCountMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["winCountMin"])}
              value={itemPayload?.winCountMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-winCountMin"
              onChange={(e) => handleData("winCountMin", e)}
              disabled={itemPayload?.winCountMin === 0}
            />
            <span className="txt">{t("winCount")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["winCountMax"])}
              value={itemPayload?.winCountMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-winCountMax"
              onChange={(e) => handleData("winCountMax", e)}
              disabled={itemPayload?.winAmountMax === 0}
            />
          </div>
          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={!!itemPayload?.winSumMin || !!itemPayload?.winSumMax}
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.winSumMin === 0 ||
                    itemPayload?.winSumMin === undefined) &&
                  (itemPayload?.winSumMax === 0 ||
                    itemPayload?.winSumMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            winSumMin: isDisabled ? 1 : 0,
                            winSumMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            winSumMin: isDisabled ? 1 : 0,
                            winSumMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["winSumMin"])}
              value={itemPayload?.winSumMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-winSumMin"
              onChange={(e) => handleData("winSumMin", e)}
              disabled={itemPayload?.winSumMin === 0}
            />
            <span className="txt">{t("winSum")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["winSumMax"])}
              value={itemPayload?.winSumMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-winSumMax"
              onChange={(e) => handleData("winSumMax", e)}
              disabled={itemPayload?.winAmountMax === 0}
            />
          </div>

          <div className="segments-rules-form-box-item">
            <SwitchBtn
              isActive={!!itemPayload?.winAvgMin || !!itemPayload?.winAvgMax}
              handleClick={() => {
                const isDisabled =
                  (itemPayload?.winAvgMin === 0 ||
                    itemPayload?.winAvgMin === undefined) &&
                  (itemPayload?.winAvgMax === 0 ||
                    itemPayload?.winAvgMax === undefined);
                dispatch(
                  // @ts-ignore
                  handleUpdateFields({
                    betRules: payloadFull?.betRules
                      ? [
                          ...payloadFull.betRules.filter(
                            (el) => el.currency !== currency
                          ),
                          {
                            ...itemPayload,
                            winAvgMin: isDisabled ? 1 : 0,
                            winAvgMax: isDisabled ? 1 : 0,
                          },
                        ]
                      : [
                          {
                            ...itemPayload,
                            winAvgMin: isDisabled ? 1 : 0,
                            winAvgMax: isDisabled ? 1 : 0,
                          },
                        ],
                  })
                );
              }}
            />
            <InnerInput
              hasError={Boolean(fieldErrors?.["winAvgMin"])}
              value={itemPayload?.winAvgMin?.toString() ?? ""}
              placeholder={t("Min")}
              label={t("Min")}
              type="number"
              id="segment-winAvgMin"
              onChange={(e) => handleData("winAvgMin", e)}
              disabled={itemPayload?.winAvgMin === 0}
            />
            <span className="txt">{t("winAvg")}</span>
            <InnerInput
              hasError={Boolean(fieldErrors?.["winAvgMax"])}
              value={itemPayload?.winAvgMax?.toString() ?? ""}
              placeholder={t("Max")}
              label={t("Max")}
              type="number"
              id="segment-winAvgMax"
              onChange={(e) => handleData("winAvgMax", e)}
              disabled={itemPayload?.winAvgMax === 0}
            />
          </div>
          <div className="segments-rules-switcher">
            <SwitchBtn
              activeTxt={t("active")}
              isActive={itemPayload?.isActive === 1}
              handleClick={() =>
                handleData(
                  "isActive",
                  itemPayload?.isActive && itemPayload?.isActive === 1 ? 0 : 1
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentRulesBetTab;
